/**
 * Product Catalog AI empty-state / intro screen (catalogue-only).
 *
 * Restores the richer hero from the original Brisque intro — gradient avatar,
 * headline + subtitle, capability chips, and icon-led suggested-prompt cards with
 * hover affordance — minus the reports capability (this chatbot is catalogue-only).
 */
import React from 'react';
import { Icon, FONT } from './atoms';
import { STARTER_PROMPTS } from '../data/scenarios';
import type { PCCta } from '../../../interface/ProductCatalogChatbotInterface';

/** Catalogue capabilities only — no Reports. */
const CAPABILITIES: { icon: string; label: string; color: string }[] = [
    { icon: 'inventory_2', label: 'Out of stock', color: 'var(--orange)' },
    { icon: 'visibility', label: 'Visibility', color: '#7C3AED' },
    { icon: 'schedule', label: 'Sessions', color: '#0EA5E9' },
];

const PC_INTRO_SUBTITLE =
    "Tell me to update your menu — check availability, 86 items, and manage channel visibility and sessions. I'll always confirm before making any changes.";

const menuIcon = (p: string): string => {
    const t = p.toLowerCase();
    if (/out of stock|86|oos|unavailable/.test(t)) return 'inventory_2';
    if (/visib|hide|show/.test(t)) return 'visibility';
    if (/session|friday|lunch|dinner|breakfast|weekend/.test(t)) return 'schedule';
    if (/why|isn'?t|not showing/.test(t)) return 'help';
    return 'restaurant_menu';
};

const STYLE = `
.pci-wrap{font-family:${FONT};animation:pciFade .35s ease-out}
@keyframes pciFade{from{opacity:0;transform:translateY(6px)}to{opacity:1;transform:translateY(0)}}
@keyframes pciGlow{0%,100%{box-shadow:0 6px 18px rgba(210,58,59,.28)}50%{box-shadow:0 8px 26px rgba(124,58,237,.30)}}
.pci-card{display:flex;align-items:center;gap:11px;width:100%;text-align:left;padding:10px 12px;border:1px solid var(--card-border);border-radius:12px;background:var(--surface);cursor:pointer;transition:border-color .15s,background .15s,transform .15s;font-family:${FONT}}
.pci-card:hover{border-color:var(--accent);background:var(--accent-soft,rgba(210,58,59,.06));transform:translateX(2px)}
.pci-card:hover .pci-arrow{opacity:1;transform:translateX(0)}
.pci-arrow{margin-left:auto;opacity:0;transform:translateX(-4px);transition:opacity .15s,transform .15s;color:var(--accent);flex-shrink:0}
.pci-chip{display:inline-flex;align-items:center;gap:6px;padding:6px 11px;border-radius:999px;background:var(--bg);border:1px solid var(--divider);font-size:11.5px;font-weight:600;color:var(--t2)}
`;

export const PCIntro: React.FC<{ onAsk: (p: string) => void; ctas?: PCCta[] }> = ({ onAsk, ctas }) => {
    // Prefer the backend-served quick actions (§Item 6); fall back to the built-in starter
    // prompts if they haven't loaded (or the endpoint is unavailable) so the intro is never bare.
    const items: { key: string; label: string; prompt: string; icon: string; color: string; hint?: string }[] =
        ctas && ctas.length
            ? ctas.map((c) => ({ key: c.id, label: c.label, prompt: c.prompt, icon: c.icon, color: c.color, hint: c.hint }))
            : STARTER_PROMPTS.map((p, i) => ({ key: `starter-${i}`, label: p, prompt: p, icon: menuIcon(p), color: '#7C3AED' }));

    return (
    <div className="pci-wrap" style={{ padding: '6px 2px 8px' }}>
        <style>{STYLE}</style>

        {/* Hero */}
        <div style={{ position: 'relative', textAlign: 'center', padding: '10px 8px 6px' }}>
            <div style={{ position: 'absolute', inset: 0, background: 'radial-gradient(120px 80px at 50% 0%, rgba(210,58,59,.08), transparent 70%)', pointerEvents: 'none' }} />
            <div style={{ position: 'relative', width: 56, height: 56, borderRadius: 18, margin: '0 auto 12px', background: 'linear-gradient(145deg, var(--accent), #7C3AED)', display: 'flex', alignItems: 'center', justifyContent: 'center', animation: 'pciGlow 3.5s ease-in-out infinite' }}>
                <Icon name="auto_awesome" size={28} color="#fff" />
            </div>
            <div style={{ fontSize: 18, fontWeight: 800, color: 'var(--t1)', letterSpacing: -0.2 }}>Hi, I&apos;m Brisque AI</div>
            <div style={{ fontSize: 12.5, color: 'var(--t2)', lineHeight: 1.5, maxWidth: 320, margin: '6px auto 0' }}>{PC_INTRO_SUBTITLE}</div>

            {/* Capability chips */}
            <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: 7, marginTop: 14 }}>
                {CAPABILITIES.map((c) => (
                    <span key={c.label} className="pci-chip">
                        <Icon name={c.icon} size={14} color={c.color} />{c.label}
                    </span>
                ))}
            </div>
        </div>

        {/* Suggested prompts / quick actions */}
        <div style={{ marginTop: 16 }}>
            <div style={{ fontSize: 10.5, fontWeight: 800, letterSpacing: 0.6, color: 'var(--t3)', textTransform: 'uppercase', margin: '0 2px 8px' }}>Manage your menu</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
                {items.map((it) => (
                    <button key={it.key} type="button" className="pci-card" onClick={() => onAsk(it.prompt)}>
                        <span style={{ width: 26, height: 26, borderRadius: 8, background: 'var(--bg)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                            <Icon name={it.icon} size={15} color={it.color} />
                        </span>
                        <span style={{ display: 'flex', flexDirection: 'column', minWidth: 0, flex: 1 }}>
                            <span style={{ fontSize: 12.5, fontWeight: 500, color: 'var(--t1)', lineHeight: 1.3 }}>{it.label}</span>
                            {it.hint && <span style={{ fontSize: 10.5, color: 'var(--t3)', lineHeight: 1.3, marginTop: 1 }}>{it.hint}</span>}
                        </span>
                        <span className="pci-arrow"><Icon name="arrow_forward" size={15} color="var(--accent)" /></span>
                    </button>
                ))}
            </div>
        </div>
    </div>
    );
};
